import { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { ChevronDown } from "lucide-react";
import { usePage } from "@inertiajs/react";

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  to { opacity: 0.9; }
`;

const bounce = keyframes`
  0%, 100% { transform: translateX(-50%) translateY(0); }
  50% { transform: translateX(-50%) translateY(10px); }
`;

const zoomIn = keyframes`
  0% { transform: scale(1); }
  100% { transform: scale(1.08); }
`;

const HeroSection = styled.section`
  position: relative;
  height: 100vh;
  min-height: 700px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  @media (max-width: 768px) {
    min-height: 600px;
  }
`;

const HeroBackground = styled.div`
  position: absolute;
  inset: 0;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image: url(${(props) => props.$imageUrl});
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    animation: ${zoomIn} 25s ease-in-out infinite alternate;
    transition: background-image 0.8s ease;
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      180deg,
      rgba(26, 26, 26, 0.4) 0%,
      rgba(26, 26, 26, 0.5) 50%,
      rgba(26, 26, 26, 0.7) 100%
    );
  }
`;

const HeroContent = styled.div`
  position: relative;
  text-align: center;
  max-width: 950px;
  padding: 0 24px;
  z-index: 1;
`;

const Subtitle = styled.h2`
  letter-spacing: 5px;
  text-transform: uppercase;
  color: #c9a86c;
  margin-bottom: 24px;
  opacity: 0;
  animation: ${fadeInUp} 1s ease forwards;
  animation-delay: 0.3s;
`;

const Title = styled.h1`
  font-size: 68px;
  font-weight: 300;
  color: #ffffff;
  margin-bottom: 28px;
  line-height: 1.1;
  letter-spacing: -1px;
  opacity: 0;
  animation: ${fadeInUp} 1s ease forwards;
  animation-delay: 0.5s;

  strong {
    font-weight: 600;
  }

  @media (max-width: 768px) {
    font-size: 44px;
  }

  @media (max-width: 480px) {
    font-size: 34px;
  }
`;

const Description = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.8;
  margin-bottom: 48px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  opacity: 0;
  animation: ${fadeInUp} 1s ease forwards;
  animation-delay: 0.7s;
  font-weight: 300;
  letter-spacing: 0.3px;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const CTAButtons = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;
  opacity: 0;
  animation: ${fadeInUp} 1s ease forwards;
  animation-delay: 0.9s;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
  }
`;

const PrimaryButton = styled.button`
  background: #c9a86c;
  color: #1a1a1a;
  border: none;
  padding: 18px 48px;
  border-radius: 0;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  letter-spacing: 2px;
  text-transform: uppercase;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );
    transition: left 0.6s ease;
  }

  &:hover::before {
    left: 100%;
  }

  &:hover {
    background: #d4b67a;
    transform: translateY(-3px);
    box-shadow: 0 15px 40px rgba(201, 168, 108, 0.4);
  }

  span {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 480px) {
    width: 100%;
    max-width: 280px;
  }
`;

const SecondaryButton = styled.button`
  background: transparent;
  color: #faf8f5;
  border: 1px solid rgba(255, 255, 255, 0.5);
  padding: 18px 48px;
  border-radius: 0;
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  letter-spacing: 2px;
  text-transform: uppercase;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 0;
    height: 100%;
    background: rgba(255, 255, 255, 0.1);
    transition: width 0.4s ease;
  }

  &:hover::before {
    width: 100%;
  }

  &:hover {
    border-color: #faf8f5;
    transform: translateY(-3px);
  }

  span {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 480px) {
    width: 100%;
    max-width: 280px;
  }
`;

const ScrollIndicator = styled.div`
  position: absolute;
  bottom: 50px;
  left: 50%;
  transform: translateX(-50%);
  cursor: pointer;
  opacity: 0;
  animation:
    ${fadeIn} 1s ease forwards,
    ${bounce} 2.5s ease-in-out infinite;
  animation-delay: 1.4s, 2.5s;

  svg {
    color: #c9a86c;
  }

  &:hover {
    opacity: 1;
  }
`;

const DotIndicators = styled.div`
  position: absolute;
  bottom: 100px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 10px;
  z-index: 2;
`;

const Dot = styled.button`
  width: ${(props) => (props.$active ? "28px" : "8px")};
  height: 8px;
  border-radius: 4px;
  background: ${(props) =>
    props.$active ? "#c9a86c" : "rgba(255, 255, 255, 0.4)"};
  border: none;
  cursor: pointer;
  padding: 0;
  transition: all 0.4s ease;

  &:hover {
    background: ${(props) =>
      props.$active ? "#c9a86c" : "rgba(255, 255, 255, 0.7)"};
  }
`;

const Hero = () => {
  const { landing_content } = usePage().props;

  const heading = landing_content?.heading || "The Eco-Tourism City Experience";
  const subheading =
    landing_content?.subheading ||
    "A modern eco hostel in the heart of the city where nature meets technology. Experience sustainable luxury with stunning urban views.";
  const images = landing_content?.hero_banner_img_urls || [];

  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (images.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentIndex((i) => (i + 1) % images.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [images.length]);

  const scrollToBooking = () => {
    const element = document.getElementById("booking");
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToAbout = () => {
    const element = document.getElementById("about");
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <HeroSection id="home">
      <HeroBackground $imageUrl={images[currentIndex]} />
      <HeroContent>
        <Subtitle>Welcome to Hebe Hotel</Subtitle>
        <Title dangerouslySetInnerHTML={{ __html: heading }} />
        <Description>{subheading}</Description>
        <CTAButtons>
          <PrimaryButton onClick={scrollToBooking}>
            <span>Book Your Stay</span>
          </PrimaryButton>
          <SecondaryButton onClick={scrollToAbout}>
            <span>Discover More</span>
          </SecondaryButton>
        </CTAButtons>
      </HeroContent>

      {images.length > 1 && (
        <DotIndicators>
          {images.map((_, i) => (
            <Dot
              key={i}
              $active={i === currentIndex}
              onClick={() => setCurrentIndex(i)}
            />
          ))}
        </DotIndicators>
      )}

      <ScrollIndicator onClick={scrollToBooking}>
        <ChevronDown size={32} />
      </ScrollIndicator>
    </HeroSection>
  );
};

export default Hero;
